import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import {
  Compass,
  Sun,
  RefreshCw,
  CheckCircle2,
  XCircle,
  MapPin,
  AlertTriangle,
  Heart,
  Sparkles,
  Quote,
  History,
  User,
  LogOut,
  Menu,
  X,
  Flame,
  Star,
  Share2,
  BarChart3,
  Copy,
  Check,
  Crown,
  Volume2,
  VolumeX,
  Pause,
  Play,
  MessageSquare,
  MapPinned,
  ChevronRight,
  Dumbbell,
  Hexagon,
  ExternalLink,
  CloudSun,
  Cloud,
  CloudRain,
  Snowflake,
  Wind,
  Sunrise,
  Sunset,
  Thermometer,
  Moon,
  BookOpen,
  Twitter,
  MessageCircle,
  Mail,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [guidance, setGuidance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });
  const [affirmation, setAffirmation] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareText, setShareText] = useState("");
  const [copied, setCopied] = useState(false);
  const [favoriting, setFavoriting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [dailyQuote, setDailyQuote] = useState(null);
  const [weatherInfo, setWeatherInfo] = useState(null);
  const [cosmic, setCosmic] = useState(null);
  const audioRef = useRef(null);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  // Location detection — re-detect a fresh position on every login/dashboard mount
  // and refresh the location-aware widgets so they never stay stuck on the last place.
  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          let city = "";
          let country = "";
          // Best-effort reverse geocode — but still update even if it fails
          try {
            const geoRes = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const geoData = await geoRes.json();
            city = geoData.address?.city || geoData.address?.town || geoData.address?.village || geoData.address?.county || "";
            country = geoData.address?.country || "";
          } catch (geoErr) {
            // reverse geocode failed — proceed with coords only
          }
          await api.post("/user/location", { latitude, longitude, city, country });
          // Refresh location-dependent widgets immediately so the UI reflects the new place
          api.get("/user/cosmic-conditions").then(r => setCosmic(r.data)).catch(() => {});
          api.get("/user/weather").then(r => { if (r.data.has_location) setWeatherInfo(r.data); }).catch(() => {});
        } catch (err) {
          // Location is optional — fail silently
        }
      },
      () => {}, // Permission denied — that's fine
      { timeout: 8000, enableHighAccuracy: true, maximumAge: 0 }
    );
  }, []);

  const fetchData = async () => {
    try {
      const [guidanceRes, streakRes, affirmationRes] = await Promise.all([
        api.get("/guidance/today"),
        api.get("/streak"),
        api.get("/affirmation"),
      ]);
      setGuidance(guidanceRes.data);
      setStreak(streakRes.data);
      setAffirmation(affirmationRes.data);

      // Fetch secondary data (non-blocking)
      api.get("/quote/daily").then(r => setDailyQuote(r.data)).catch(() => {});
      api.get("/user/weather").then(r => {
        if (r.data.has_location) setWeatherInfo(r.data);
      }).catch(() => {});
      api.get("/user/cosmic-conditions").then(r => setCosmic(r.data)).catch(() => {});
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load your guidance");
    } finally {
      setLoading(false);
    }
  };

  const regenerateGuidance = async () => {
    setRegenerating(true);
    try {
      const response = await api.post("/guidance/regenerate");
      setGuidance(response.data);
      toast.success("New guidance generated!");
    } catch (error) {
      console.error("Error regenerating guidance:", error);
      toast.error("Failed to regenerate guidance");
    } finally {
      setRegenerating(false);
    }
  };

  const toggleFavorite = async () => {
    if (!guidance) return;
    setFavoriting(true);
    try {
      const response = await api.post(`/favorites/${guidance.guidance_id}`);
      setGuidance({ ...guidance, is_favorited: response.data.favorited });
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorite");
    } finally {
      setFavoriting(false);
    }
  };

  const handleShare = async () => {
    if (!guidance) return;
    try {
      const response = await api.get(`/share/${guidance.guidance_id}`);
      setShareText(response.data.share_text);
      setShareDialogOpen(true);
    } catch (error) {
      console.error("Error getting share text:", error);
      toast.error("Failed to prepare share content");
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const createShareLink = async (type, content) => {
    try {
      const res = await api.post("/social/share", { type, content });
      const shareUrl = `${window.location.origin}/shared/${res.data.share_id}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard!");
    } catch (err) {
      toast.error("Failed to create share link");
    }
  };

  const shareToTwitter = (text) => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const shareToWhatsApp = (text) => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const shareViaEmail = (subject, body) => {
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const getWeatherIcon = (condition) => {
    if (!condition) return <CloudSun className="w-5 h-5" />;
    const c = condition.toLowerCase();
    if (c.includes("rain") || c.includes("drizzle") || c.includes("shower")) return <CloudRain className="w-5 h-5" />;
    if (c.includes("snow")) return <Snowflake className="w-5 h-5" />;
    if (c.includes("cloud") || c.includes("overcast") || c.includes("fog")) return <Cloud className="w-5 h-5" />;
    if (c.includes("wind")) return <Wind className="w-5 h-5" />;
    return <Sun className="w-5 h-5" />;
  };

  // Text-to-Speech functions (API-powered)
  const createGuidanceText = () => {
    if (!guidance) return "";
    
    const userName = user?.first_name || user?.name || "friend";
    let text = `Good ${getGreeting().split(" ")[1]}, ${userName}. Here is your daily guidance. `;
    text += `${guidance.headline}. `;
    text += `${guidance.overview} `;
    text += `Things to do today: ${guidance.do_today.join(". ")}. `;
    text += `Things to avoid today: ${guidance.avoid_today.join(". ")}. `;
    text += `Today's wise saying: ${guidance.wise_saying}. `;
    text += `Spiritual insight: ${guidance.spiritual_guidance}. `;
    text += `A gentle reminder: ${guidance.warning}. `;
    text += `And finally, your encouragement: ${guidance.encouragement}`;
    
    return text;
  };

  const speakGuidance = async () => {
    if (isSpeaking && !isPaused && audioRef.current) {
      audioRef.current.pause();
      setIsPaused(true);
      return;
    }

    if (isPaused && audioRef.current) {
      audioRef.current.play();
      setIsPaused(false);
      return;
    }

    // Start fresh — generate via API
    setTtsLoading(true);
    toast.info("Generating premium audio...");

    try {
      const text = createGuidanceText();
      const res = await api.post("/tts/generate", { text });
      const { audio_base64 } = res.data;

      const audio = new Audio(`data:audio/mp3;base64,${audio_base64}`);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsSpeaking(true);
        setIsPaused(false);
        setTtsLoading(false);
      };
      audio.onended = () => {
        setIsSpeaking(false);
        setIsPaused(false);
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        setIsPaused(false);
        setTtsLoading(false);
        toast.error("Audio playback failed");
      };

      await audio.play();
      toast.success("Reading your guidance...");
    } catch (err) {
      console.error("TTS error:", err);
      setTtsLoading(false);
      // Fallback to browser speech synthesis
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
        const text = createGuidanceText();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.onstart = () => { setIsSpeaking(true); setIsPaused(false); };
        utterance.onend = () => { setIsSpeaking(false); setIsPaused(false); };
        utterance.onerror = () => { setIsSpeaking(false); setIsPaused(false); };
        window.speechSynthesis.speak(utterance);
        toast.success("Reading your guidance...");
      } else {
        toast.error("Failed to generate audio");
      }
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    setIsPaused(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Request push notification permission
  useEffect(() => {
    const subscribeToPush = async () => {
      if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
        return;
      }

      if (Notification.permission === "default" && user?.notifications_enabled) {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;
      }

      if (Notification.permission !== "granted") return;

      try {
        // Get VAPID public key from backend
        const vapidRes = await api.get("/notifications/vapid-key");
        const vapidKey = vapidRes.data.public_key;
        if (!vapidKey) return;

        // Convert base64url to Uint8Array
        const urlBase64ToUint8Array = (base64String) => {
          const padding = '='.repeat((4 - base64String.length % 4) % 4);
          const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
          const rawData = window.atob(base64);
          return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
        };

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });

        const subJson = subscription.toJSON();
        await api.post("/notifications/subscribe", {
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth,
          }
        });
      } catch (error) {
        console.log("Push subscription skipped:", error.message);
      }
    };

    if (user) {
      subscribeToPush();
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-white/70 font-body">Preparing your guidance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Compass className="w-5 h-5 text-[#070B14]" />
            </div>
            <span className="font-display text-xl text-white hidden sm:block">SoulGuide</span>
          </div>

          {/* Streak Badge */}
          {streak.current_streak > 0 && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30">
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-body text-orange-300">{streak.current_streak} day streak</span>
            </div>
          )}

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-2">
            <Button
              data-testid="nav-premium"
              variant="ghost"
              onClick={() => navigate("/premium")}
              className={`${user?.is_premium ? 'text-[#D4AF37]' : 'text-white/70'} hover:text-[#D4AF37] hover:bg-[#D4AF37]/10`}
            >
              <Crown className="w-4 h-4 mr-2" />
              {user?.is_premium ? 'Premium' : 'Upgrade'}
            </Button>
            <Button
              data-testid="nav-analytics"
              variant="ghost"
              onClick={() => navigate("/analytics")}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Insights
            </Button>
            <Button
              data-testid="nav-chat"
              variant="ghost"
              onClick={() => navigate("/chat")}
              className="text-[#8B5CF6] hover:text-[#C4B5FD] hover:bg-[#8B5CF6]/10"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              AI Advisor
            </Button>
            <Button
              data-testid="nav-favorites"
              variant="ghost"
              onClick={() => navigate("/favorites")}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <Star className="w-4 h-4 mr-2" />
              Favorites
            </Button>
            <Button
              data-testid="nav-history"
              variant="ghost"
              onClick={() => navigate("/history")}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
            <Button
              data-testid="nav-friends"
              variant="ghost"
              onClick={() => navigate("/friends")}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <Users className="w-4 h-4 mr-2" />
              Friends
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="p-2">
                  <img
                    src={user?.picture || `https://ui-avatars.com/api/?name=${user?.first_name || user?.name}&background=D4AF37&color=070B14`}
                    alt="Profile"
                    className="w-8 h-8 rounded-full"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#0A101D] border-white/10 text-white">
                <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer hover:bg-white/10">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/wellness")} className="cursor-pointer hover:bg-white/10">
                  <Dumbbell className="w-4 h-4 mr-2" />
                  Wellness
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/friends")} className="cursor-pointer hover:bg-white/10">
                  <Users className="w-4 h-4 mr-2" />
                  Friends
                </DropdownMenuItem>
                {user?.is_premium && (
                  <DropdownMenuItem onClick={() => navigate("/premium/tarot")} className="cursor-pointer hover:bg-white/10">
                    <Hexagon className="w-4 h-4 mr-2" />
                    Tarot
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="cursor-pointer hover:bg-white/10 text-red-400">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            className="md:hidden p-2 text-white rounded-lg hover:bg-white/10"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 bg-[#070B14]/95 backdrop-blur-xl">
            <nav className="p-4 space-y-2">
              {streak.current_streak > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 mb-3">
                  <Flame className="w-4 h-4 text-orange-400" />
                  <span className="text-sm font-body text-orange-300">{streak.current_streak} day streak</span>
                </div>
              )}
              <Button
                variant="ghost"
                onClick={() => { navigate("/premium"); setMobileMenuOpen(false); }}
                className={`w-full justify-start ${user?.is_premium ? 'text-[#D4AF37]' : 'text-white/70'} hover:text-[#D4AF37] hover:bg-[#D4AF37]/10`}
              >
                <Crown className="w-4 h-4 mr-2" />
                {user?.is_premium ? 'Premium Features' : 'Upgrade to Premium'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate("/analytics"); setMobileMenuOpen(false); }}
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Insights
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate("/chat"); setMobileMenuOpen(false); }}
                className="w-full justify-start text-[#8B5CF6] hover:text-[#C4B5FD] hover:bg-[#8B5CF6]/10"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                AI Advisor
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate("/favorites"); setMobileMenuOpen(false); }}
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
              >
                <Star className="w-4 h-4 mr-2" />
                Favorites
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate("/history"); setMobileMenuOpen(false); }}
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
              >
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate("/friends"); setMobileMenuOpen(false); }}
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
              >
                <Users className="w-4 h-4 mr-2" />
                Friends
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate("/profile"); setMobileMenuOpen(false); }}
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate("/wellness"); setMobileMenuOpen(false); }}
                className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
              >
                <Dumbbell className="w-4 h-4 mr-2" />
                Wellness
              </Button>
              {user?.is_premium && (
                <Button
                  variant="ghost"
                  onClick={() => { navigate("/premium/tarot"); setMobileMenuOpen(false); }}
                  className="w-full justify-start text-[#D4AF37] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                >
                  <Hexagon className="w-4 h-4 mr-2" />
                  Tarot Readings
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full justify-start text-red-400 hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Greeting & Affirmation */}
        <div className="animate-fade-in-up">
          <div className="flex items-center gap-2 text-[#D4AF37] mb-2">
            <Sun className="w-5 h-5" />
            <span className="text-xs tracking-[0.2em] uppercase font-body">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-white mb-4">
            {getGreeting()}, <span className="gold-text">{user?.first_name || user?.name}</span>
          </h1>
          
          {/* Daily Affirmation Card */}
          {affirmation && (
            <div className="glass-card p-4 border-l-4 border-[#8B5CF6]">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#C4B5FD]" />
                <span className="text-xs text-[#C4B5FD] uppercase tracking-wider font-body">Today's Affirmation</span>
              </div>
              <p className="text-white/90 font-body italic">{affirmation.affirmation}</p>
            </div>
          )}

          {/* Cosmic Conditions Row — moon phase always shows so the widget never goes dark */}
          {cosmic && (
            <div className="glass-card p-4 animate-fade-in-up" style={{ animationDelay: "0.05s" }} data-testid="cosmic-conditions">
              {cosmic.has_location && cosmic.city ? (
                <div className="flex items-center gap-2 mb-3">
                  <MapPinned className="w-3.5 h-3.5 text-white/30" />
                  <p className="text-white/50 font-body text-xs">{cosmic.city}{cosmic.country ? `, ${cosmic.country}` : ""}</p>
                </div>
              ) : null}
              <div className="flex items-center gap-5 overflow-x-auto no-scrollbar">
                <div className="flex flex-col items-center min-w-[64px]" data-testid="cosmic-moon">
                  <span className="text-2xl leading-none">{cosmic.moon?.emoji}</span>
                  <span className="text-white font-body text-sm font-semibold mt-1">{cosmic.moon?.illumination}%</span>
                  <span className="text-white/40 font-body text-[10px] text-center">{cosmic.moon?.phase_name}</span>
                </div>
                {cosmic.has_location && cosmic.weather?.temperature != null && (
                  <div className="flex flex-col items-center min-w-[64px]">
                    <Thermometer className="w-4 h-4 text-amber-300" />
                    <span className="text-white font-body text-sm font-semibold mt-1">{Math.round(cosmic.weather.temperature)}°</span>
                    <span className="text-white/40 font-body text-[10px] text-center">{(cosmic.weather.condition || "Now").trim()}</span>
                  </div>
                )}
                {cosmic.has_location && cosmic.temp?.high != null && (
                  <div className="flex flex-col items-center min-w-[64px]">
                    <Thermometer className="w-4 h-4 text-purple-300" />
                    <span className="text-white font-body text-sm font-semibold mt-1">{Math.round(cosmic.temp.high)}° / {Math.round(cosmic.temp.low ?? 0)}°</span>
                    <span className="text-white/40 font-body text-[10px] text-center">High / Low</span>
                  </div>
                )}
                {cosmic.has_location && cosmic.sun?.sunrise && (
                  <div className="flex flex-col items-center min-w-[64px]">
                    <Sunrise className="w-4 h-4 text-yellow-400" />
                    <span className="text-white font-body text-sm font-semibold mt-1">{cosmic.sun.sunrise}</span>
                    <span className="text-white/40 font-body text-[10px] text-center">Sunrise</span>
                  </div>
                )}
                {cosmic.has_location && cosmic.sun?.sunset && (
                  <div className="flex flex-col items-center min-w-[64px]">
                    <Sunset className="w-4 h-4 text-orange-400" />
                    <span className="text-white font-body text-sm font-semibold mt-1">{cosmic.sun.sunset}</span>
                    <span className="text-white/40 font-body text-[10px] text-center">Sunset</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Moon Ritual suggestion — tied to the live moon phase */}
          {cosmic?.moon?.ritual && (
            <div className="gold-accent-card p-5 animate-fade-in-up" style={{ animationDelay: "0.07s" }} data-testid="moon-ritual">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl leading-none">{cosmic.moon.emoji}</span>
                <div>
                  <span className="text-xs text-[#D4AF37] uppercase tracking-wider font-body">Moon Ritual</span>
                  <p className="font-display text-lg sm:text-xl text-white leading-tight">{cosmic.moon.ritual.title}</p>
                </div>
              </div>
              <p className="text-white/70 font-body text-sm leading-relaxed">{cosmic.moon.ritual.practice}</p>
            </div>
          )}
        </div>

        {guidance && (
          <>
            {/* Headline Card */}
            <div className="gold-accent-card p-6 sm:p-8 animate-fade-in-up relative" style={{ animationDelay: "0.1s" }}>
              {/* Action buttons */}
              <div className="absolute top-4 right-4 flex gap-2">
                {/* Text-to-Speech Button */}
                {isSpeaking ? (
                  <>
                    <button
                      data-testid="pause-btn"
                      onClick={speakGuidance}
                      className="p-2 rounded-full bg-[#8B5CF6]/20 text-[#C4B5FD] hover:bg-[#8B5CF6]/30 transition-all"
                    >
                      {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                    </button>
                    <button
                      data-testid="stop-btn"
                      onClick={stopSpeaking}
                      className="p-2 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
                    >
                      <VolumeX className="w-5 h-5" />
                    </button>
                  </>
                ) : ttsLoading ? (
                  <button
                    data-testid="tts-loading"
                    disabled
                    className="p-2 rounded-full bg-[#8B5CF6]/20 text-[#C4B5FD] animate-pulse"
                  >
                    <Volume2 className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    data-testid="listen-btn"
                    onClick={speakGuidance}
                    className="p-2 rounded-full bg-white/5 text-white/50 hover:text-[#C4B5FD] hover:bg-[#8B5CF6]/10 transition-all"
                    title="Listen to guidance"
                  >
                    <Volume2 className="w-5 h-5" />
                  </button>
                )}
                <button
                  data-testid="favorite-btn"
                  onClick={toggleFavorite}
                  disabled={favoriting}
                  className={`p-2 rounded-full transition-all ${
                    guidance.is_favorited
                      ? "bg-[#D4AF37]/20 text-[#D4AF37]"
                      : "bg-white/5 text-white/50 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  }`}
                >
                  <Star className={`w-5 h-5 ${guidance.is_favorited ? "fill-current" : ""}`} />
                </button>
                <button
                  data-testid="share-btn"
                  onClick={handleShare}
                  className="p-2 rounded-full bg-white/5 text-white/50 hover:text-[#C4B5FD] hover:bg-[#8B5CF6]/10 transition-all"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
              
              <h2 className="font-display text-2xl sm:text-3xl text-white mb-4 pr-28">{guidance.headline}</h2>
              <p className="text-white/70 font-body leading-relaxed">{guidance.overview}</p>
            </div>

            {/* Guidance Grid */}
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {/* Do Today */}
              <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.2s" }} data-testid="do-today-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="font-display text-xl text-white">Do Today</h3>
                </div>
                <ul className="space-y-3">
                  {guidance.do_today.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-white/80 font-body">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Avoid Today */}
              <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.25s" }} data-testid="avoid-today-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-red-400" />
                  </div>
                  <h3 className="font-display text-xl text-white">Avoid Today</h3>
                </div>
                <ul className="space-y-3">
                  {guidance.avoid_today.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-white/80 font-body">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Go Today */}
              <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.3s" }} data-testid="go-today-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#C4B5FD]" />
                  </div>
                  <h3 className="font-display text-xl text-white">Where to Go</h3>
                </div>
                <ul className="space-y-3">
                  {guidance.go_today.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-white/80 font-body">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C4B5FD] mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Avoid Places */}
              <div className="glass-card p-6 animate-fade-in-up" style={{ animationDelay: "0.35s" }} data-testid="avoid-places-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-orange-400" />
                  </div>
                  <h3 className="font-display text-xl text-white">Where to Avoid</h3>
                </div>
                <ul className="space-y-3">
                  {guidance.avoid_places.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-white/80 font-body">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Wise Saying */}
            <div
              className="gold-accent-card p-6 sm:p-8 animate-fade-in-up relative overflow-hidden"
              style={{ animationDelay: "0.4s" }}
              data-testid="wise-saying-card"
            >
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: "url(https://images.unsplash.com/photo-1647101734210-ff5d79813069?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjBnb2xkfGVufDB8fHx8MTc3NTUxNTgyMXww&ixlib=rb-4.1.0&q=85)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="relative">
                <Quote className="w-8 h-8 text-[#D4AF37] mb-4" />
                <p className="font-display text-2xl sm:text-3xl text-white italic leading-relaxed">
                  "{guidance.wise_saying}"
                </p>
              </div>
            </div>

            {/* Quote of the Day Sage */}
            {dailyQuote && (
              <div className="glass-card p-6 sm:p-8 animate-fade-in-up border-l-4 border-[#D4AF37]/50" style={{ animationDelay: "0.42s" }} data-testid="sage-quote-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37]/30 to-[#8B5CF6]/20 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-white">Quote of the Day Sage</h3>
                    <span className="text-[#D4AF37]/60 text-xs font-body uppercase tracking-wider">{dailyQuote.theme}</span>
                  </div>
                </div>
                <p className="font-display text-xl text-white/90 italic leading-relaxed mb-3">
                  "{dailyQuote.quote}"
                </p>
                <p className="text-white/40 font-body text-sm mb-4">— {dailyQuote.author}</p>
                {dailyQuote.reflection && (
                  <p className="text-white/60 font-body text-sm border-t border-white/5 pt-3">{dailyQuote.reflection}</p>
                )}
                {/* Share buttons for quote */}
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/5">
                  <span className="text-white/30 text-xs font-body mr-1">Share:</span>
                  <button
                    data-testid="share-quote-twitter"
                    onClick={() => shareToTwitter(`"${dailyQuote.quote}" — The Sage\n\n#SoulGuide #DailyWisdom #QuoteOfTheDay`)}
                    className="p-2 rounded-lg bg-white/5 text-white/40 hover:text-sky-400 hover:bg-sky-400/10 transition-all"
                    title="Share on X/Twitter"
                  >
                    <Twitter className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="share-quote-whatsapp"
                    onClick={() => shareToWhatsApp(`"${dailyQuote.quote}" — The Sage\n\n✨ From SoulGuide`)}
                    className="p-2 rounded-lg bg-white/5 text-white/40 hover:text-green-400 hover:bg-green-400/10 transition-all"
                    title="Share on WhatsApp"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="share-quote-copy"
                    onClick={async () => {
                      await navigator.clipboard.writeText(`"${dailyQuote.quote}" — The Sage`);
                      toast.success("Quote copied!");
                    }}
                    className="p-2 rounded-lg bg-white/5 text-white/40 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
                    title="Copy quote"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="share-quote-link"
                    onClick={() => createShareLink("quote", { quote: dailyQuote.quote, author: dailyQuote.author, theme: dailyQuote.theme, reflection: dailyQuote.reflection })}
                    className="p-2 rounded-lg bg-white/5 text-white/40 hover:text-[#C4B5FD] hover:bg-[#8B5CF6]/10 transition-all"
                    title="Create shareable link"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Spiritual Guidance */}
            <div className="glass-card p-6 sm:p-8 animate-fade-in-up" style={{ animationDelay: "0.45s" }} data-testid="spiritual-guidance-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#C4B5FD]" />
                </div>
                <h3 className="font-display text-xl text-white">Spiritual Insight</h3>
              </div>
              <p className="text-white/80 font-body leading-relaxed">{guidance.spiritual_guidance}</p>
            </div>

            {/* Warning */}
            <div className="glass-card p-6 border-l-4 border-[#D4AF37] animate-fade-in-up" style={{ animationDelay: "0.5s" }} data-testid="warning-card">
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle className="w-5 h-5 text-[#D4AF37]" />
                <h3 className="font-display text-lg text-white">Gentle Reminder</h3>
              </div>
              <p className="text-white/70 font-body">{guidance.warning}</p>
            </div>

            {/* Encouragement */}
            <div className="glass-card p-6 sm:p-8 animate-fade-in-up" style={{ animationDelay: "0.55s" }} data-testid="encouragement-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-xl text-white">Encouragement</h3>
              </div>
              <p className="text-white/80 font-body leading-relaxed text-lg">{guidance.encouragement}</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fade-in-up" style={{ animationDelay: "0.6s" }}>
              <Button
                data-testid="reflect-btn"
                onClick={() => navigate("/reflection")}
                className="btn-gold flex-1 text-lg py-6"
              >
                <Heart className="w-5 h-5 mr-2" />
                Reflect on Your Day
              </Button>
              <Button
                data-testid="regenerate-btn"
                onClick={regenerateGuidance}
                disabled={regenerating}
                className="btn-secondary flex-1 text-lg py-6"
              >
                {regenerating ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2" />
                    New Guidance
                  </>
                )}
              </Button>
            </div>

            {/* AI Chat Advisor CTA */}
            {user?.is_premium && (
              <button
                data-testid="chat-cta"
                onClick={() => navigate("/chat")}
                className="w-full glass-card p-6 animate-fade-in-up text-left group hover:border-[#8B5CF6]/30 transition-all"
                style={{ animationDelay: "0.65s" }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#D4AF37] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-lg text-white group-hover:text-[#D4AF37] transition-colors">Ask Your AI Advisor</h3>
                    <p className="text-white/50 font-body text-sm">Get personalized spiritual guidance through conversation</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-[#D4AF37] transition-colors flex-shrink-0" />
                </div>
              </button>
            )}

            {/* Quick Actions Grid */}
            <div className="grid grid-cols-2 gap-4 animate-fade-in-up" style={{ animationDelay: "0.7s" }}>
              {user?.is_premium && (
                <button
                  data-testid="tarot-quick-btn"
                  onClick={() => navigate("/premium/tarot")}
                  className="glass-card p-5 text-left group hover:border-[#D4AF37]/30 transition-all"
                >
                  <Hexagon className="w-8 h-8 text-[#D4AF37] mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-display text-sm text-white">Tarot Reading</h3>
                  <p className="text-white/40 font-body text-xs mt-1">Draw your cards</p>
                </button>
              )}
              <button
                data-testid="wellness-quick-btn"
                onClick={() => navigate("/wellness")}
                className="glass-card p-5 text-left group hover:border-green-500/30 transition-all"
              >
                <Dumbbell className="w-8 h-8 text-green-400 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-display text-sm text-white">Wellness</h3>
                <p className="text-white/40 font-body text-xs mt-1">Track your wellbeing</p>
              </button>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-4 sm:px-6 py-8 text-center">
        <p className="text-xs text-white/30 font-body">
          SoulGuide provides guidance for personal inspiration only. Not medical, legal, or financial advice.
        </p>
      </footer>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="bg-[#0A101D] border-white/10 text-white max-w-md" aria-describedby="share-dialog-description">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Share Today's Wisdom</DialogTitle>
          </DialogHeader>
          <p id="share-dialog-description" className="sr-only">Copy and share today's guidance with friends</p>
          <div className="space-y-4 mt-4">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-white/80 font-body whitespace-pre-line text-sm">{shareText}</p>
            </div>
            {/* Platform Sharing Row */}
            <div className="flex gap-2">
              <button
                data-testid="share-twitter"
                onClick={() => { shareToTwitter(shareText); setShareDialogOpen(false); }}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-sky-500/10 border border-sky-500/20 text-sky-400 hover:bg-sky-500/20 transition-all font-body text-sm"
              >
                <Twitter className="w-4 h-4" /> X / Twitter
              </button>
              <button
                data-testid="share-whatsapp"
                onClick={() => { shareToWhatsApp(shareText); setShareDialogOpen(false); }}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 hover:bg-green-500/20 transition-all font-body text-sm"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </button>
              <button
                data-testid="share-email"
                onClick={() => { shareViaEmail("Today's Wisdom from SoulGuide", shareText); setShareDialogOpen(false); }}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all font-body text-sm"
              >
                <Mail className="w-4 h-4" /> Email
              </button>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={copyToClipboard}
                className="btn-gold flex-1"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Text
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  createShareLink("wisdom", {
                    wise_saying: guidance?.wise_saying,
                    headline: guidance?.headline,
                    encouragement: guidance?.encouragement,
                    overview: guidance?.overview,
                  });
                  setShareDialogOpen(false);
                }}
                className="btn-secondary flex-1"
                data-testid="share-link-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Share Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
